
import { Component, Vue, Prop, Provide, Watch, Ref } from "vue-property-decorator"
import { namespace, Getter, Mutation, State } from "vuex-class"

import CaseFilters from "@/components/CaseList/CaseFilters.vue"
import QuickCreate from "@/components/CaseDetails/QuickCreate.vue"
import ErrorDisplay from "@/components/Controls/ErrorDisplay.vue"

import HeaderBar from "./HeaderBar.vue"
import SideBar from "./SideBar.vue"

import { CurrentUser, SystemUser } from "@/types/user"

import { ViewsService } from "@/services/viewsService"
import QuickCreateMenu from "./QuickCreateMenu.vue"
import { TenantFeatures } from "@/types/tenantFeatures"
import UpdatesViewer from "@/components/Updates/UpdatesViewer.vue"
import { ErrorInfo } from "@/types/error"
import { ScheduleMessage } from "@/types/schedule"
import { DateTime } from "luxon"

const caseStore = namespace("cases")

@Component({
	components: {
		"quick-create-menu": QuickCreateMenu,
		"case-filters": CaseFilters,
		"quick-create": QuickCreate,
		"header-bar": HeaderBar,
		"side-bar": SideBar,
		"updates-viewer": UpdatesViewer,
		"error-display": ErrorDisplay
	}
})
export default class Page extends Vue {
	@Ref()
	updatesViewer!: UpdatesViewer

	@Provide("currentUser")
	get currentUser (): CurrentUser {
		return this.me
	}

	@Provide("featureEnabled")
	featureEnabled (feature: TenantFeatures) {
		return this.getIsFeatureEnabled(feature)
	}

	@Prop(String) private readonly title!: string;

	@Prop({ type: Boolean, default: false })
	isDisplayingAdminPortal!: boolean;

	@State
	public systemUser!: SystemUser | null

	@State
	public userLoadError!: ErrorInfo | null

	@State
	public apiError!: ErrorInfo | null
	@State
	public scheduleToastMessage!: ScheduleMessage | null

	@Getter("getMe")
	public me!: CurrentUser;

	@Getter("getHasIntegrations") hasIntegrations!: boolean
	@Getter getIsFeatureEnabled!: (feature: TenantFeatures) => boolean
	@Getter("getScreenWidth")
	public screenWidth!: number;

	@caseStore.State viewsService!: ViewsService;
	@Getter("getCaseName") private readonly caseName!: string;
	@Getter("getRecordName") private readonly recordName!: string;
	@Getter getCheckForUpdates!: boolean;
	@Mutation
	private setCheckForUpdates!: (checkForUpdates: boolean) => void

	// related to User Updates
	public showUpdatesIfAppropriate: boolean = false
	public displayAllUpdates: boolean = false

	private get hasFooterSlot () {
		return !!this.$slots["footer-left"] || !!this.$slots["footer-right"]
	}

	public get userName (): string {
		if (this.systemUser) return this.systemUser.name
		return this.me?.name ?? ""
	}

	public mounted () {
		this.onApiErrorChange(this.apiError)
	}

	@Watch("getCheckForUpdates", { immediate: true })
	public onCheckForUpdatesChanged (newValue: boolean) {
		if (newValue && !this.isDisplayingAdminPortal) {
			this.triggerUpdatesViewer(false)
			this.setCheckForUpdates(false)
		}
	}

	@Watch("apiError")
	public onApiErrorChange (apiError: ErrorInfo | null) {
		if (apiError) {
			this.$bvToast.show("api-error-toast")
		} else {
			this.$bvToast.hide("api-error-toast")
		}
	}

	public updatesVisibilityChanged (updatesVisibility: boolean) {
		if (updatesVisibility === false) {
			this.showUpdatesIfAppropriate = false
			this.displayAllUpdates = false
		}
	}

	private triggerUpdatesViewer (displayAllUpdates: boolean = this.displayAllUpdates) {
		this.displayAllUpdates = displayAllUpdates
		if (this.showUpdatesIfAppropriate) {
			// user already has the modal open, trigger a background check
			this.updatesViewer.runBackgroundCheck()
		} else {
			this.showUpdatesIfAppropriate = true
		}
	}

	public get userUpdatesFeatureEnabled (): boolean {
		return this.featureEnabled(TenantFeatures.UserUpdates)
	}

	@Watch("scheduleToastMessage")
	scheduleToastMessageChanged (message: ScheduleMessage) {
		if (message && this.getIsFeatureEnabled(TenantFeatures.ScheduleForecastPopup)) {
			this.$bvToast.toast(message.message, {
				title: "Schedule",
				variant: message.variant,
				solid: true
			})
		}
	}
}

